// ‘Copyright ©2022, Cheers Interactive Pvt Ltd. All rights reserved. //
// File Description :Content card
// ---------------------------------------------------------------------------------
// Creation Details
// Date Created :21 Nov 2023
// Author:Lawrence Anthony
// FDD Ref:
// TDD Ref:
// RTM Ref:
// Test Case Ref:

// id title date label industryTag entityType infoText profileImage video videoDuration

import React, { useState, useEffect, useRef } from "react";
import CommonDateTagSection from "../CommonDateTagSection/CommonDateTagSection";
import BookmarkInfoPair from "../BookmarkInfoPair/BookmarkInfoPair";
import ImageFile from "./imageFile";
import classes from "./contentCard.module.css";
import ReactHtmlParser from "react-html-parser";
import { makeStyles } from "@mui/styles";
import { getPages } from "../../../middleware/services/deliverablesApi";
import ModalVideo from "react-modal-video";
import { accessRightActionCheck } from "utilities";
import { COMMON_ACCESS_ACTION, CONTENT_TYPE } from "../../../constants";

const useStyles = makeStyles(() => ({
  root: {
    height: 5,
    borderRadius: 5,
    background: "#d3cdcd !important",
  },
  colorPrimary: {
    backgroundColor: 200,
  },
  bar: {
    borderRadius: 5,
    backgroundColor: "#f94f5e !important",
  },
}));

const ContentCard = ({
  item,
  redirectToPage,
  contentCardClass,
  sgfFilters,
  imageClassName,
  imageStyles,
  titleStyles,
  alwaysShowFullCard = false,
  showProgressBar = false,
  isSiteSearch = false,
  showLikeSection = false,
  liked,
  count,
  handleCount,
}) => {
  const tooltipRef = useRef(null);
  const tooltipTextRef = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [isOpenVideo, setOpenVideo] = useState(false);
  const [videoSource, setVideoSource] = useState("");
  const [videoId, setVideoId] = useState("");
  const [listingActionAccess, setListingActionAccess] = useState([]);

  const accessAdministrator = listingActionAccess.includes(COMMON_ACCESS_ACTION.REPORT_ADMINISTRATOR);
  /**
   * The function opens a registration link if the video status is "Upcoming" and opens a modal if the
   * status is anything else.
   */
  const getVideoStatus = (item) => {
    if (item.status === "Upcoming" && item.ribbon === "Webinar") {
      window.open(item.registrationLink, "_blank", "noreferrer");
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    return false;
  };

  function mergeStrings(string1, string2) {
    const emMatches = string2.match(/<em>(.*?)<\/em>/g);
    if (emMatches) {
      emMatches.forEach((emMatch) => {
        const textWithinEm = emMatch.match(/<em>(.*?)<\/em>/)[1];
        const regex = new RegExp(`\\b${textWithinEm}\\b`, "g");
        string1 = string1.replace(regex, emMatch);
      });
    }
    const filteredString = string1.replace(/<em>(<em>)+/g, "<em>").replace(/(<\/em>)+<\/em>/g, "</em>");
    return filteredString;
  }

  const getHighlightedTitle = (item) => {
    if (!item.highlighting) return item.title;
    if (item.entityType === "company" && item.highlighting.name) return mergeStrings(item.title, item.highlighting.name[0]);
    if (item.highlighting.title) return mergeStrings(item.title, item.highlighting.title[0]);
    return item.title;
  };

  const getVideoContent = async (item) => {
    setVideoSource("");
    setVideoId("");
    setOpenVideo(false);
    let pages = await getPages(item.id);
    let videoSrc = [];
    if (!pages.length) return redirectToPage();
    pages.forEach((page) => {
      for (let block of page?.pages) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = block.pageContentHtml;
        const iframes = tempDiv.querySelectorAll("iframe");
        if (iframes?.length > 0) {
          for (const iframe of iframes) {
            const src = iframe.src;
            if (src.includes("youtube")) {
              // Use a regular expression to extract the video ID
              const match = src.match(/\/embed\/([a-zA-Z0-9_-]+)/);
              if (match) {
                const videoId = match[1];
                // Duration of Youtube
                if (videoId) {
                  videoSrc.push({ videoSource: "youtube", videoId: videoId });
                }
              }
            }
            if (src.includes("vimeo")) {
              // Use a regular expression to extract the video ID
              const match = src.match(/\/(\d+)(?:\?|#|$)/);
              if (match) {
                const videoId = match[1];
                // Duration of Vimeo
                if (videoId) {
                  videoSrc.push({ videoSource: "vimeo", videoId: videoId });
                }
              }
            }
          }
        }
      }
    });
    if (videoSrc.length) {
      setVideoSource(videoSrc[0]?.videoSource);
      setVideoId(videoSrc[0]?.videoId);
      setOpenVideo(true);
    } else {
      return redirectToPage();
    }
  };

  const handleMouseEnter = () => {
    tooltipRef.current.style.display = "block";
  };

  const handleMouseLeave = () => {
    tooltipRef.current.style.display = "none";
  };

  useEffect(() => {
    if (tooltipTextRef.current) {
      tooltipTextRef.current.addEventListener("mouseenter", handleMouseEnter);
      tooltipTextRef.current.addEventListener("mouseleave", handleMouseLeave);
    }
  }, [tooltipTextRef.current]);

  useEffect(() => {
    let interfaceName = "Listing";
    let actionAccess = accessRightActionCheck("Deliverables", interfaceName);
    setListingActionAccess(actionAccess);
  }, []);

  return (
    <div className={`${classes.recent_content_card} ${!alwaysShowFullCard ? `recent_mobile_card` : ``}`} onClick={handleClose}>
      <div className={`${classes.recent_content_img} recent_content_img`}>
        <BookmarkInfoPair
          className={`${classes.pairOuterDiv} bookmarkOuter`}
          item={item}
          entityType={item.label}
          entityTitle={item.title}
          infoText={item.infoText}
          showThreeDot={true}
          deliverableAccessUsers={[
            ...(item.deliverableTaskWriters || []), // Check if it's iterable or provide an empty array
            ...(item.deliverableTaskReviewers || []),
            ...(item.deliverableTaskPublishers || []),
            ...(item.deliverableCreatedBy || []),
          ]}
          accessAdministrator={accessAdministrator}
        />
        <ImageFile
          fileName={item.profileImage}
          entityType={item.entityType}
          className={imageClassName}
          imageStyle={imageStyles}
          redirectToPage={redirectToPage}
          getVideoStatus={getVideoStatus}
          item={item}
        />

        {item.video && item.entityType === "video" && (
          <>
            <img
              onClick={() => {
                getVideoStatus(item);
              }}
              className={classes.play_icon}
              src="/assets/icon-play.svg"
              alt="img"
            ></img>

            {item.video.videoSource === "YouTube" ? (
              <ModalVideo
                channel="youtube"
                youtube={{
                  autoplay: 1,
                  mute: 1,
                }}
                isOpen={isOpen}
                videoId={item.video.videoId}
                onClose={() => {
                  setOpen(false);
                }}
              />
            ) : (
              <ModalVideo
                channel="vimeo"
                vimeo={{
                  autoplay: 1,
                  mute: 1,
                }}
                isOpen={isOpen}
                videoId={item.video.videoId}
                onClose={() => {
                  setOpen(false);
                }}
              />
            )}
          </>
        )}
        {!item?.video && item.label === CONTENT_TYPE.WEBINARS_INTERVIEWS_PODCASTS && (
          <>
            <img
              onClick={() => {
                getVideoContent(item);
              }}
              className={classes.play_icon}
              src="/assets/icon-play.svg"
              alt="img"
            ></img>
            {videoSource === "youtube" && (
              <ModalVideo
                channel="youtube"
                youtube={{
                  autoplay: 1,
                  mute: 1,
                }}
                isOpen={isOpenVideo}
                videoId={videoId}
                onClose={() => {
                  setOpenVideo(false);
                }}
              />
            )}
            {videoSource === "vimeo" && (
              <ModalVideo
                channel="vimeo"
                vimeo={{
                  autoplay: 1,
                  mute: 1,
                }}
                isOpen={isOpenVideo}
                videoId={videoId}
                onClose={() => {
                  setOpenVideo(false);
                }}
              />
            )}
          </>
        )}
        <div className={`d-flex justify-content-end position-absolute ${classes.clock}`}>
          {item.readTime !== 0 && item.readTime && (
            <div className="d-flex align-items-center rounded bg-dark text-white py-1 px-2 ms-1 ms-md-2">
              <img src="/assets/clock-white.svg" alt="clockIcon" width={12}></img>
              <span className="ms-1 text-xs" style={{ marginLeft: "5px" }}>
                {item.readTime} min
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={`content-card-like ${classes.recent_content_desc} ${classes.recent_content_desc_site_search}`}>
        <h4 onClick={redirectToPage} id="RECENT">
          <div className={`${classes.recent_content_site_search_title} not-italic`}>{ReactHtmlParser(getHighlightedTitle(item))}</div>
        </h4>
        <CommonDateTagSection
          date={item.date}
          label={item.label}
          tags={item.industryTag}
          sgfFilters={sgfFilters}
          ribbon={item.ribbon}
          highlighting={item.highlighting}
        ></CommonDateTagSection>

        {/* Currently not being used as common search does not give this data. Will need this in future */}
        {/* {item.isPublished ? (
          <span style={{ backgroundColor: `#D2EFE8`, padding: "2px 5px", borderRadius: "3px", color: "#0CB963", fontSize: "14px" }}>Published</span>
        ) : (
          <span
            style={{
              backgroundColor: `#FDD14133`,
              padding: "2px 5px",
              borderRadius: "3px",
              color: "#FDD141",
              fontSize: "14px",
            }}
          >
            In Progress
          </span>
        )} */}
        {item?.deliverableTaskWriters?.length ||
        item?.deliverableTaskReviewers?.length ||
        item?.deliverableTaskPublishers?.length ||
        item?.deliverableCreatedBy ||
        item?.newsCreatedBy ||
        item?.companyCreatedBy ||
        item?.videoCreatedBy ? (
          <div className={`${classes.date_summary} mobile_date_summary`}>
            {item?.deliverableTaskWriters?.length ? (
              <span className={`${classes.writer}`} title="Writer">
                <img src="/assets/search-creator-icon.svg" alt="creatorIcon" width={12}></img>
                <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>{item.deliverableTaskWriters[0]?.split("|")[0]}</p>
              </span>
            ) : (
              item?.deliverableCreatedBy?.length && (
                <span className={`${classes.writer}`} title="Writer">
                  <img src="/assets/search-creator-icon.svg" alt="creatorIcon" width={12}></img>
                  <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>{item.deliverableCreatedBy[0].split("|")[0]}</p>
                </span>
              )
            )}
            {item?.deliverableTaskReviewers?.length && (
              <span className={`${classes.reviewer}`} title="Reviewer">
                <img src="/assets/search-reviewer-icon.svg" alt="reviewerIcon" width={12}></img>
                <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>{item.deliverableTaskReviewers[0]?.split("|")[0]}</p>
              </span>
            )}
            {item?.deliverableTaskPublishers?.length && (
              <span className={`${classes.publisher}`} title="Publisher">
                <img src="/assets/search-publisher-icon.svg" alt="publisherIcon" width={12}></img>
                <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>
                  {item.deliverableTaskPublishers[0]?.split("|")[0]?.split(" ")[0]?.length > 7
                    ? `${item.deliverableTaskPublishers[0]?.split("|")[0].slice(0, 7)}...`
                    : item.deliverableTaskPublishers[0]?.split("|")[0]}
                </p>
              </span>
            )}
            {item?.newsCreatedBy?.length && (
              <span className={`${classes.writer}`} title="Writer">
                <img src="/assets/search-creator-icon.svg" alt="creatorIcon" width={12}></img>
                <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>{item.newsCreatedBy[0]?.split("|")[0]}</p>
              </span>
            )}
            {item?.companyCreatedBy?.length && (
              <span className={`${classes.writer}`} title="Writer">
                <img src="/assets/search-creator-icon.svg" alt="creatorIcon" width={12}></img>
                <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>{item.companyCreatedBy[0].split("|")[0]}</p>
              </span>
            )}
            {item?.videoCreatedBy?.length && (
              <span className={`${classes.writer}`} title="Writer">
                <img src="/assets/search-creator-icon.svg" alt="creatorIcon" width={12}></img>
                <p style={{ paddingLeft: "1px", paddingRight: "1px" }}>{item.videoCreatedBy[0].split("|")[0]}</p>
              </span>
            )}
          </div>
        ) : (
          <div style={{ height: "29px" }}></div>
        )}
      </div>
    </div>
  );
};

export default ContentCard;
